import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Button,
  FormControlLabel,
  Switch
} from "@mui/material";
import InfoButton from "../../Tooltip/InfoTextField";
import { PaymentProviderConfigViewModel } from "../../../Types/ExportTypes";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../Context/Contexts";

type Props = {
  viewModel: PaymentProviderConfigViewModel | null;
  onSave: (vm: PaymentProviderConfigViewModel) => void;
};

const QliroPaymentProviderWizard: React.FC<Props> = ({ viewModel, onSave }) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const [localViewModel, setLocalViewModel] = useState<PaymentProviderConfigViewModel>(viewModel ?? {});

  const handleChange = (field: keyof PaymentProviderConfigViewModel, value: string | null) => {
    setLocalViewModel((prev) => ({ ...prev, [field]: value }));
  };

  const handleFinish = () => {
    if (!localViewModel.description || localViewModel.description.trim() === "") {
      showSnackbar(t("help.descriptionRequired"), "warning");
      return;
    }
    onSave(localViewModel);
  };

  const cancelWizard = () => {
    window.history.back();
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 600, mx: "auto", px: 2 }}>
      <Typography variant="h6" gutterBottom>
        Qliro
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label={t("common.description")}
            value={localViewModel.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
            fullWidth
            margin="normal"
            error={!localViewModel.description || localViewModel.description.trim() === ""}
            helperText={
              !localViewModel.description?.trim() ? t("help.descriptionRequired") : ""
            }
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label={t("common.qliroInfo1")}
            value={localViewModel.paymentProviderInfo1 || ""}
            onChange={(e) => handleChange("paymentProviderInfo1", e.target.value)}
            fullWidth
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <InfoButton infoText={t("help.qliroUrl")} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label={t("common.qliroInfo2")}
            value={localViewModel.paymentProviderInfo2 || ""}
            onChange={(e) => handleChange("paymentProviderInfo2", e.target.value)}
            fullWidth
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <InfoButton infoText={t("help.qliroMerchantApiKey")} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label={t("common.qliroInfo3")}
            value={localViewModel.paymentProviderInfo3 || ""}
            onChange={(e) => handleChange("paymentProviderInfo3", e.target.value)}
            fullWidth
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <InfoButton infoText={t("help.qliroSecret")} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <Box sx={{ marginTop: 2, textAlign: "center" }}>
        <Button variant="contained" color="primary" onClick={handleFinish} sx={{ mr: 2 }}>
          {t("common.finish")}
        </Button>
        <Button variant="contained" color="warning" onClick={cancelWizard}>
          {t("common.cancelWizard")}
        </Button>
      </Box>
    </Box>
  );
};

export default QliroPaymentProviderWizard;
